<template>
  <div class="ak-steps" :class="{ 'ak-steps--inverse': inverse }">
    <nav class="ak-steps__nav" :class="mainClasses">
      <ul class="ak-steps__nav-list">
        <li
          v-for="(stepItem, index) in stepItems"
          v-show="stepItem.visible"
          :key="index"
          class="ak-step"
          :class="{
            'is-active': activeStep === index,
            'is-complete': activeStep > index,
          }"
        >
          <div
            class="ak-step-link"
            :class="{ 'cursor-pointer': isItemClickable(stepItem, index) }"
            @click="isItemClickable(stepItem, index) && stepClick(index)"
          >
            <div class="ak-step__indicator"></div>
            <div class="ak-step__label">
              <span class="ak-step__label--title">{{ stepItem.label }}</span>
            </div>
          </div>
        </li>
      </ul>
    </nav>
    <section
      class="ak-step-content"
      :class="{ 'is-transitioning': isTransitioning }"
    >
      <slot />
    </section>
    <slot
      name="navigation"
      :previous="navigationProps.previous"
      :next="navigationProps.next"
    >
      <nav v-if="hasNavigation" class="step-navigation">
        <ak-button-group class="mt color-grey-light">
          <ak-button
            role="button"
            class="pagination-previous"
            :disabled="navigationProps.previous.disabled"
            :aria-label="ariaPreviousLabel"
            size="small"
            color="grey-light"
            @click.prevent="navigationProps.previous.action"
          >
            <ak-icon :type="iconPrev" aria-hidden="true" />
          </ak-button>
          <ak-button
            role="button"
            class="pagination-next"
            :disabled="navigationProps.next.disabled"
            :aria-label="ariaNextLabel"
            size="small"
            color="grey-light"
            @click.prevent="navigationProps.next.action"
          >
            <ak-icon :type="iconNext" aria-hidden="true" />
          </ak-button>
        </ak-button-group>
      </nav>
    </slot>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue'
import ButtonGroup from '../buttonGroup/ButtonGroup.vue'
import Button from '../button/Button.vue'
import Icon from '../icon/Icon.vue'
import SlotComponent from '../../utils/SlotComponent'
import config from '../../utils/config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-steps type="info" label="Email">
   *   <ak-step-item label="Step 1" clickable>
   *      <h1>Step 1</h1>
   *   <ak-step-item>
   *   <ak-step-item label="Step 2" clickable>
   *      <h1>Step 2</h1>
   *   <ak-step-item>
   * </ak-steps>
   */
  name: 'AkSteps',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [SlotComponent.name]: SlotComponent,
    [ButtonGroup.name]: ButtonGroup,
    [Button.name]: Button,
  },

  provide() {
    return {
      akSteps: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    modelValue: {
      type: Number,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @default null
     * @description Inverse step
     */
    inverse: {
      type: Boolean,
      default: null,
    },

    /**
     * @type {Boolean}
     * @default false
     * @description Steps have slide animation
     */
    animated: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @default false
     * @description Destroy stepitem on hide
     */
    destroyOnHide: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @default
     * @description Icon to use for navigation button
     */
    iconPrev: {
      type: String,
      default: config.defaultIconPrev,
    },

    /**
     * @type {String}
     * @default
     * @description Icon to use for navigation button
     */
    iconNext: {
      type: String,
      default: config.defaultIconNext,
    },

    /**
     * @type {String}
     * @default
     * @description Next and previous buttons below the component. You can use
     * this property if you want to use your own custom navigation items.
     */
    hasNavigation: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @default undefined
     * @description Accessibility label for the page link. If passed, this
     * text will be prepended to the number of the page.
     */
    ariaNextLabel: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String}
     * @default undefined
     * @description Accessibility label for the current page link. If passed,
     * this text will be prepended to the current page.
     */
    ariaPreviousLabel: {
      type: String,
      default: undefined,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue', 'change'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const defaultSlots = ref([])
    const contentHeight = ref(0)
    const isTransitioning = computed(() => false)
    const isSteps = ref(true)
    const stepItems = ref([])

    /**
     * @type {Object}
     * @description A reactive object to contain the active step model
     * value.
     */
    const activeStep = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
        emit('change', value)
      },
    })

    /**
     * @returns {Array}
     * @description Returns the classes for the steps component
     */
    const mainClasses = computed(() => {
      return {
        [`ak-step--${props.type}`]: props.type,
      }
    })

    /**
     * @returns {Array}
     * @returns Returns the step items for the component, but in reverse
     * order
     */
    const reversedStepItems = computed(() => {
      return stepItems.value.slice().reverse()
    })

    /**
     * @returns {Boolean}
     * @description Check the first visible step index.
     */
    const firstVisibleStepIndex = computed(() => {
      return stepItems.value.map((step) => step.visible).indexOf(true)
    })

    /**
     * @returns {Boolean}
     * @description Check if previous button is available.
     */
    const hasPrev = computed(() => {
      return (
        firstVisibleStepIndex.value >= 0 &&
        activeStep.value > firstVisibleStepIndex.value
      )
    })

    /**
     * @returns {Number}
     * @description Check the last visible step index.
     */
    const lastVisibleStepIndex = computed(() => {
      let idx = reversedStepItems.value
        .map((step) => step.visible)
        .indexOf(true)
      if (idx >= 0) {
        return stepItems.value.length - 1 - idx
      }
      return idx
    })

    /**
     * @returns {Boolean}
     * @description Check if next button is available.
     */
    const hasNext = computed(() => {
      return (
        lastVisibleStepIndex.value >= 0 &&
        activeStep.value < lastVisibleStepIndex.value
      )
    })

    /**
     * @returns {Object}
     * @description Returns the properties to set on the navigation
     * for the steps component
     */
    const navigationProps = computed(() => {
      return {
        previous: {
          disabled: !hasPrev.value,
          action: prev,
        },
        next: {
          disabled: !hasNext.value,
          action: next,
        },
      }
    })

    /**
     * @description Change the active step and emit change event.
     */
    const changeStep = (newIndex) => {
      if (activeStep.value === newIndex) return
      if (newIndex > stepItems.value.length) {
        throw new Error(
          'The index you trying to set is bigger than the steps length'
        )
      }
      activeStep.value = newIndex
    }

    /**
     * @description Return if the step should be clickable or not.
     */
    const isItemClickable = (stepItem, index) => {
      if (stepItem.clickable === undefined) {
        return activeStep.value > index
      }
      return stepItem.clickable
    }

    /**
     * @description Step click listener, emit input event and change
     * active step.
     */
    const stepClick = (value) => {
      changeStep(value)
    }

    /**
     * @description Previous button click listener.
     */
    const prev = () => {
      if (!hasPrev.value) return

      let prevItemIdx = reversedStepItems.value
        .map(
          (step, idx) =>
            stepItems.value.length - 1 - idx < activeStep.value && step.visible
        )
        .indexOf(true)

      if (prevItemIdx >= 0) {
        prevItemIdx = stepItems.value.length - 1 - prevItemIdx
      }

      changeStep(prevItemIdx)
    }

    /**
     * @description Previous button click listener.
     */
    const next = () => {
      if (!hasNext.value) return

      const nextItemIdx = stepItems.value
        .map((step, idx) => idx > activeStep.value && step.visible)
        .indexOf(true)

      changeStep(nextItemIdx)
    }

    /**
     * @type {Function}
     * @description Registers a new step in the parent steps component. This
     * will allow the parent to manage the child components as they get added.
     */
    const registerStep = (step) => {
      stepItems.value.push(step)
    }

    /**
     * @type {Function}
     * @description Un-registers an existing step in the parent steps component.
     * This will allow the parent to remove a child tab when it is no longer
     * needed.
     */
    const unregisterStep = (id) => {
      const idx = stepItems.value.findIndex((i) => i.uid === id)

      /* istanbul ignore else */
      if (idx >= 0) {
        stepItems.value.splice(idx, 1)
      }
    }

    /**
     * @description Sets the isTransitioning value to true when the TabItem
     * transition animation starts
     */
    const startTransition = () => {
      isTransitioning.value = true
    }

    /**
     * @description Sets the isTransitioning value to false when the TabItem
     * transition animation ends
     */
    const endTransition = () => {
      isTransitioning.value = false
    }

    onMounted(() => {
      /* istanbul ignore else */
      if (activeStep.value < stepItems.value.length) {
        stepItems.value[activeStep.value].activate(activeStep.value, 0)
      }
    })

    watch(activeStep, (newVal, oldVal) => {
      stepItems.value[oldVal].deactivate(oldVal, newVal)
      stepItems.value[newVal].activate(oldVal, newVal)
      emit('change', newVal)
    })

    return {
      activeStep,
      defaultSlots,
      contentHeight,
      isTransitioning,
      isSteps,
      mainClasses,
      stepItems,
      reversedStepItems,
      firstVisibleStepIndex,
      hasPrev,
      lastVisibleStepIndex,
      hasNext,
      navigationProps,
      changeStep,
      isItemClickable,
      stepClick,
      prev,
      next,
      registerStep,
      unregisterStep,
      startTransition,
      endTransition,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
$steps-color: $color-primary;
$steps-color-inverse: #fff;
$steps-dot-size: 21px;
.ak-steps {
  &__nav-list {
    display: flex;
    overflow-x: auto;
  }
}

.ak-step {
  flex-grow: 1;
  text-align: center;
  padding: 0.5rem;
  color: rgba($steps-color, 0.75);
  position: relative;
  transition: $transition;
  @media screen and #{$mq-palm} {
    &:not(.step--palm-visible) {
      display: none;
    }
  }
  @media screen and #{$mq-lap-up} {
    &:after,
    &:before {
      content: ' ';
      height: 2px;
      background-color: $color-grey-lighter;
      position: absolute;
      top: 18px;
      z-index: 0;
    }
    &:before {
      left: 0;
      right: 50%;
      margin-right: 10px; // Move out of the dot!
    }
    &:after {
      left: 50%;
      right: 0;
      margin-left: 10px; // Move out of the dot!
    }
    &:first-child {
      &:before {
        content: '';
        display: none;
      }
    }
    &:last-child {
      &:after {
        content: '';
        display: none;
      }
    }
  }

  &.is-active {
    @media screen and #{$mq-palm} {
      &:not(.step--palm-visible) {
        display: block;
      }
    }
    color: $steps-color;
    &:before {
      background-color: $color-grey-lighter;
    }

    .ak-step__indicator {
      @media screen and #{$mq-palm} {
        &:not(.ak-step__indicator--palm-visible) {
          display: none;
        }
      }
      &:after {
        transform: scale(1);
      }
    }
  }

  &.is-complete {
    color: $steps-color;
    &:after,
    &:before {
      background-color: $color-grey-lighter;
    }

    .ak-step__indicator {
      background-color: $steps-color;
    }
  }

  &--action {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: $color-grey-light;
    &:hover {
      color: $steps-color;
    }
  }

  &__indicator {
    display: inline-block;
    width: $steps-dot-size;
    height: $steps-dot-size;
    border-radius: 50%;
    border: 2px solid;
    position: relative;
    transition: $transition;
    z-index: 2;

    &:after {
      content: ' ';
      width: 11px;
      height: 11px;
      display: block;
      position: absolute;
      left: 3px;
      top: 3px;
      border-radius: 50%;
      background-color: $steps-color;
      transform: scale(0);
      transition: $transition;
    }

    &--action {
      color: $color-grey-lighter;
      box-shadow: $base-shadow-large;
      z-index: 1;
      position: relative;
      background-color: #fff;
      line-height: $steps-dot-size * 1.33;
      width: $steps-dot-size * 1.33;
      height: $steps-dot-size * 1.33;
      margin-top: -($steps-dot-size * 0.33);
      font-size: 125%;
      margin-bottom: auto;
      border: 0;
      &:after {
        content: '';
        display: none;
      }
    }
  }
  &__remove {
    left: 50%;
    width: 1.5rem;
    height: 1.5rem;
    position: absolute;
    top: -0.25rem;
    margin-left: 0.5rem;
    font-size: $font-size-smaller;
    color: $color-grey-light;
    &:hover {
      color: $color-danger;
    }
  }

  &__label {
    font-weight: bold;
    font-size: $font-size-small;
  }

  &:hover {
    color: $steps-color;
    .ak-step__indicator {
      &:after {
        transform: scale(1);
      }
    }
  }
  &--action:hover {
    &,
    .ak-step__indicator--action {
      color: $steps-color;
    }
  }
}

.ak-steps--inverse {
  .ak-step {
    color: rgba($steps-color-inverse, 0.75);
    .ak-step__indicator:after,
    &:after,
    &:before {
      background-color: $steps-color-inverse;
    }
    &.is-active {
      color: $steps-color-inverse;
      &:before {
        background-color: $steps-color-inverse;
      }

      .ak-step__indicator {
        &:after {
          background-color: $steps-color-inverse;
        }
      }
    }

    &.is-complete {
      color: $steps-color-inverse;
      &:after,
      &:before {
        background-color: $steps-color-inverse;
      }
      .ak-step__indicator {
        background-color: $steps-color-inverse;
      }
    }
  }
}

.ak-steps--vertical {
  flex-direction: column;
  overflow: visible;

  .ak-step {
    text-align: left;
    display: flex;
    align-items: center;
    padding: $spacing-small 0;

    @media screen and #{$mq-lap-up} {
      &:before {
        display: none;
      }
      &:after {
        content: ' ';
        width: 2px;
        background-color: rgba($steps-color, 0.5);
        position: absolute;
        left: -0.4%;
        height: calc(100% - 1.2rem);
        top: 70%;
      }

      &:last-child:not(.step--last) {
        &:after {
          content: ' ';
          display: block;
        }
      }
    }

    &__indicator {
      flex-shrink: 1;
      margin: 0;
      min-width: $steps-dot-size;
      @media screen and #{$mq-lap-up} {
        margin-right: $spacing-small;
      }
    }

    &__label {
      flex-grow: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 170px;
    }
    &__move {
      flex-shrink: 1;
      cursor: move;
      padding-right: $spacing-tiny;
      color: $color-grey-light;
      padding-top: 1px;
    }
    &__label-arrow {
      flex-shrink: 1;
      padding-left: $spacing-tiny;
    }
  }
}
</style>
